const API_BASE_URL = "https://api.simplif.com";

async function dashboardRequest(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(options.headers || {}),
    },
  });

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      data?.error || "Unable to load dashboard data. Please try again."
    );

    error.status = response.status;
    error.code = data?.code || null;
    error.data = data;

    throw error;
  }

  return data;
}

export function getDashboardOverview() {
  return dashboardRequest("/v1/dashboard/overview", {
    method: "GET",
  });
}

export function getDashboardEvaluations({
  limit = 50,
  offset = 0,
  verdict,
} = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (verdict) {
    params.set("verdict", verdict);
  }

  return dashboardRequest(
    `/v1/dashboard/evaluations?${params.toString()}`,
    {
      method: "GET",
    }
  );
}

export function getDashboardEvaluationDetail(evaluationId) {
  if (!evaluationId) {
    throw new Error("Evaluation ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/evaluations/${encodeURIComponent(evaluationId)}`,
    {
      method: "GET",
    }
  );
}

export function getDashboardIntegration() {
  return dashboardRequest("/v1/dashboard/integration", {
    method: "GET",
  });
}

export function provisionDashboardIntegration() {
  return dashboardRequest("/v1/dashboard/integration/provision", {
    method: "POST",
  });
}

export function rotateDashboardIntegrationSecret() {
  return dashboardRequest("/v1/dashboard/integration/rotate", {
    method: "POST",
  });
}

export { API_BASE_URL };

export function getDashboardReviews({
  limit = 100,
  offset = 0,
  status,
  priority,
  assigned_to,
} = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (status) params.set("status", status);
  if (priority) params.set("priority", priority);
  if (assigned_to) params.set("assigned_to", assigned_to);

  return dashboardRequest(
    `/v1/dashboard/reviews?${params.toString()}`,
    { method: "GET" }
  );
}

export function getDashboardReview(reviewId) {
  if (!reviewId) {
    throw new Error("Review ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/reviews/${encodeURIComponent(reviewId)}`,
    { method: "GET" }
  );
}

export function createDashboardReview(payload) {
  return dashboardRequest("/v1/dashboard/reviews", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function assignDashboardReview(reviewId, assignedTo) {
  return dashboardRequest(
    `/v1/dashboard/reviews/${encodeURIComponent(reviewId)}/assign`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ assigned_to: assignedTo }),
    }
  );
}

export function resolveDashboardReview(reviewId, payload) {
  if (!reviewId) {
    throw new Error("Review ID is required.");
  }

  if (!payload || !["fraud", "legitimate"].includes(payload.resolution)) {
    throw new Error("A valid review resolution is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/reviews/${encodeURIComponent(reviewId)}/resolve`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

export function overrideDashboardEvaluationVerdict(evaluationId, verdict) {
  if (!evaluationId) {
    throw new Error("Evaluation ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/evaluations/${encodeURIComponent(evaluationId)}/verdict`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ verdict }),
    }
  );
}

// ============================================================
// DASHBOARD RULE MANAGEMENT
// Session-authenticated. Never exposes the tenant secret API key.
// ============================================================

export function getDashboardRules({ includeDisabled = true } = {}) {
  return dashboardRequest(
    `/v1/dashboard/rules?enabled=${includeDisabled ? "false" : "true"}`,
    {
      method: "GET",
    }
  );
}

export function getDashboardRule(ruleId) {
  if (!ruleId) {
    throw new Error("Rule ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/rules/${encodeURIComponent(ruleId)}`,
    {
      method: "GET",
    }
  );
}

export function createDashboardRule(payload) {
  return dashboardRequest("/v1/dashboard/rules", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateDashboardRule(ruleId, payload) {
  if (!ruleId) {
    throw new Error("Rule ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/rules/${encodeURIComponent(ruleId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

export function deleteDashboardRule(ruleId) {
  if (!ruleId) {
    throw new Error("Rule ID is required.");
  }

  return dashboardRequest(
    `/v1/dashboard/rules/${encodeURIComponent(ruleId)}`,
    {
      method: "DELETE",
    }
  );
}

export function validateDashboardRuleCondition(condition) {
  return dashboardRequest("/v1/dashboard/rules/validate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ condition }),
  });
}

export function getDashboardRuleTemplates() {
  return dashboardRequest("/v1/dashboard/rule-templates", {
    method: "GET",
  });
}

export function instantiateDashboardRuleTemplate(
  templateKey,
  overrides = {}
) {
  if (!templateKey) {
    throw new Error("Template key is required.");
  }

  return dashboardRequest(
    "/v1/dashboard/rule-templates/instantiate",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        template_key: templateKey,
        overrides,
      }),
    }
  );
}


export function getDashboardBillingStatus() {
  return dashboardRequest("/v1/dashboard/billing", { method: "GET" });
}
